import React, { useContext, useState, useEffect } from 'react';
import AOS from 'aos';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { 
  FaTools, 
  FaCogs, 
  FaCheckCircle, 
  FaAward, 
  FaTruck, 
  FaWarehouse, 
  FaShieldAlt, 
  FaHeadset, 
  FaPhoneAlt, 
  FaWhatsapp, 
  FaNetworkWired, 
  FaGlobe,
  FaBarcode,
  FaChalkboardTeacher
} from 'react-icons/fa';
import { 
  RiCustomerService2Fill, 
  RiSettings4Fill, 
  RiTimeLine, 
  RiShieldCheckFill,
  RiSpeedUpFill
} from 'react-icons/ri';
import { store } from '../App';
import Footer from './Footer';
import '../css/Services.css';

// Image resolver helper: supports backend public URL and local public folder
const resolveImage = (imgName, fallback = '') => {
  if (!imgName) return fallback;
  if (imgName.startsWith('http://') || imgName.startsWith('https://')) return imgName;
  return `/Images/service/${imgName}`;
};

function Services() {
  const [lang] = useContext(store); // lang === true: Arabic, false: English
  const [activeTab, setActiveTab] = useState('service'); // 'service' | 'parts'
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check URL hash on load (e.g. #parts or #service)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '').toLowerCase();
      if (hash === 'parts' || hash === 'spare-parts') {
        setActiveTab('parts');
      } else if (hash === 'service' || hash === 'services') {
        setActiveTab('service');
      }
    }
  }, []);

  // Fetch dynamic services and parts data from backend
  useEffect(() => {
    const isLocal = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    // On local machine, use local backend. On deployed/live domain (e.g. sub15.sbagh.com), use the remote API
    const primaryUrl = isLocal
      ? 'http://localhost/foton-oman-backend/api/getServicePageDataBySlug/services'
      : 'https://dev10.sbagh.com/api/getServicePageDataBySlug/service-and-parts';

    const fallbackUrl = isLocal
      ? 'https://dev10.sbagh.com/api/getServicePageDataBySlug/service-and-parts'
      : 'https://dev10.sbagh.com/api/getServicePageDataBySlug/services';

    axios.get(primaryUrl)
      .then(res => {
        if (res?.data?.data) {
          setPageData(res.data.data);
        }
      })
      .catch(err => {
        console.warn('Could not load from primary backend, trying fallback:', err?.message);
        axios.get(fallbackUrl)
          .then(res => {
            if (res?.data?.data) {
              setPageData(res.data.data);
            }
          })
          .catch(e => console.error('Error fetching service page data:', e));
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {
          if (typeof AOS !== 'undefined') {
            AOS.refresh();
          }
        }, 150);
      });
  }, []);

  // Tab change handler with hash update
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (typeof window !== 'undefined' && window.history?.pushState) {
      window.history.pushState(null, '', `#${tab}`);
    }
    setTimeout(() => {
      if (typeof AOS !== 'undefined') {
        AOS.refresh();
      }
    }, 100);
  };

  // Extract all dynamic items from API response
  const rawServices = pageData?.services_list || [];
  const rawParts = pageData?.parts_list || [];
  const allServicesData = pageData?.serive_data?.content || [];

  // Fallback partitioning if services_list / parts_list not separated in legacy format
  const dynamicServices = rawServices.length > 0 
    ? rawServices 
    : allServicesData.filter(item => (item.service_type || 'service') === 'service');

  const dynamicParts = rawParts.length > 0 
    ? rawParts 
    : allServicesData.filter(item => item.service_type === 'parts');

  // Specific dynamic items
  const totalCareItem = dynamicServices[0];
  const networkItem = dynamicServices[1];
  const trainingItem = dynamicServices[2];
  const remoteSupportItem = dynamicServices[3];

  const genuinePartsItem = dynamicParts[0];
  const partsSupplyItem = dynamicParts[1];
  const whyQualityPartsItem = dynamicParts[2];
  const packagingItem = dynamicParts[3];

  // Additional items added by admin dynamically beyond the core 4
  const extraServices = dynamicServices.slice(4);
  const extraParts = dynamicParts.slice(4);

  // SEO details
  const seoTitle = pageData?.seo?.meta_title || (lang ? 'خدمات فوتون وقطع الغيار الأصلية | سلطنة عمان' : 'Foton Services & Genuine Parts | Oman');
  const seoDesc = pageData?.seo?.meta_description || (lang ? 'خدمات فوتون الشاملة وقطع الغيار الأصلية في سلطنة عمان من خلال رمال الخليج الكبرى.' : 'Foton Total Care services and genuine spare parts in Oman through Gulf Great Sands LLC.');

  return (
    <div className={`services-page-wrapper ${lang ? 'rtl' : 'ltr'}`}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content="foton, service, spare parts, total care, genuine parts, oman, gulf great sands" />
        <link rel="canonical" href={typeof window !== 'undefined' ? `${window.location.origin}/services` : 'https://sub15.sbagh.com/services'} />
      </Helmet>

      {/* ---------------- HERO BANNER ---------------- */}
      <section 
        className="services-hero-banner"
        style={{
          backgroundImage: `url(${activeTab === 'service' ? '/Images/service/service_banner.jpg' : '/Images/service/parts_banner.jpg'})`
        }}
      >
        <div className="services-hero-overlay"></div>
        <Container>
          <Row>
            <Col lg={10} className="services-hero-content" data-aos="fade-up">
              <span className="hero-badge-tag">
                {lang ? 'رمال الخليج الكبرى – الموزع الرسمي لـ فوتون' : 'Gulf Great Sands – Official Distributor of Foton'}
              </span>

              <h1 className="services-hero-title">
                {activeTab === 'service' 
                  ? (lang 
                      ? (totalCareItem?.service_name_ar || 'خدمة فوتون – الرعاية الشاملة (TOTAL CARE)') 
                      : (totalCareItem?.service_name_en || 'FOTON SERVICE – TOTAL CARE')) 
                  : (lang 
                      ? (genuinePartsItem?.service_name_ar || 'قطع غيار فوتون الأصلية المعتمدة') 
                      : (genuinePartsItem?.service_name_en || 'FOTON GENUINE PARTS'))}
              </h1>

              <p className="services-hero-subtitle">
                {activeTab === 'service' 
                  ? (lang 
                      ? (totalCareItem?.service_description_ar || 'نظام خدمة قياسي عالمي، شبكة متكاملة من مراكز الصيانة، ودعم فني متخصص لضمان أقصى كفاءة تشغيلية لأساطيل مركباتكم التجارية في سلطنة عمان.')
                      : (totalCareItem?.service_description_en || 'Global standard service system, nationwide authorized service network, and certified technical support to keep your commercial fleet operating at peak performance.'))
                  : (lang
                      ? (genuinePartsItem?.service_description_ar || 'أكثر من 160,000 صنف من قطع الغيار الأصلية المعتمدة من المصنع، مع أنظمة لوجستية عالمية تضمن سرعة التوريد، وأعلى معايير المتانة والسلامة.')
                      : (genuinePartsItem?.service_description_en || 'Over 160,000 genuine parts varieties engineered to exact factory specifications, backed by world-class warehousing and rapid logistics.'))}
              </p>

              <div className="hero-highlights-row">
                <div className="hero-highlight-chip">
                  <FaGlobe />
                  <span>{lang ? 'أكثر من 1,220 منفذ خدمة حول العالم' : '1,220+ Global Service Outlets'}</span>
                </div>
                <div className="hero-highlight-chip">
                  <FaShieldAlt />
                  <span>{lang ? 'قطع غيار أصلية 100% مع ضمان معتمد' : '100% Genuine Parts with Warranty'}</span>
                </div>
                <div className="hero-highlight-chip">
                  <FaHeadset />
                  <span>{lang ? 'تشخيص ذكي ودعم فني متواصل' : 'IDE Expert Diagnostics & 24/7 Support'}</span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ---------------- STICKY TAB SELECTOR ---------------- */}
      <nav className="services-tab-nav-wrapper">
        <Container>
          <div className="services-tab-bar">
            <button 
              type="button"
              className={`service-tab-btn ${activeTab === 'service' ? 'active' : ''}`}
              onClick={() => handleTabChange('service')}
            >
              <RiCustomerService2Fill />
              <span>{lang ? 'خدمة فوتون (SERVICE)' : 'FOTON SERVICE'}</span>
            </button>

            <button 
              type="button"
              className={`service-tab-btn ${activeTab === 'parts' ? 'active' : ''}`}
              onClick={() => handleTabChange('parts')}
            >
              <RiSettings4Fill />
              <span>{lang ? 'قطع الغيار الأصلية (PARTS)' : 'FOTON GENUINE PARTS'}</span>
            </button>
          </div>
        </Container>
      </nav>

      {/* ========================================================
          TAB 1: FOTON SERVICE
          ======================================================== */}
      {activeTab === 'service' && (
        <main className="tab-content-service">
          
          {/* Section 1: TOTAL CARE */}
          <section className="service-showcase-section">
            <Container>
              <div className="service-showcase-card" data-aos="fade-up">
                <Row className="g-0 align-items-stretch">
                  <Col lg={7} className="showcase-text-col">
                    <span className="showcase-badge">
                      {lang ? 'رعاية العملاء العالمية' : 'Global Care System'}
                    </span>
                    <h2 className="showcase-title">
                      {lang ? (totalCareItem?.service_name_ar || 'الرعاية الشاملة (TOTAL CARE)') : (totalCareItem?.service_name_en || 'TOTAL CARE')}
                    </h2>
                    <p className="showcase-desc">
                      {lang 
                        ? (totalCareItem?.service_description_ar || 'نعتمد على شبكة توزيع عالمية لتزويد عملائنا بمنتجات وخدمات وإكسسوارات وتدريب ودعم فني وفقاً لأعلى المعايير. أطلقت فوتون خدمة "توتال كير" تدريجياً، مع 13 مركز توزيع إقليمي و12 مركز تدريب على الخدمة، وأكثر من 1500 شبكة خدمة خارجية، مما يضمن تلبية احتياجات العملاء باستمرار.')
                        : (totalCareItem?.service_description_en || 'We rely on the worldwide distribution network to provide users with standard products, service, accessories, training and technical support. FOTON has launched "Total Care" service gradually. With 13 self-support regional distribution centers, 12 regional service training centers, over 1,500 overseas service networks, FOTON has constantly improved its global service system to satisfy the client care requirements and provide deeper experiences. FOTON focuses on customer needs, experience, and personalized professional services for every commercial vehicle client.')}
                    </p>

                    <ul className="showcase-features-list">
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? '13 مركز توزيع إقليمي (RDC) و12 مركز تدريب متخصص حول العالم' : '13 self-support Regional Distribution Centers & 12 training centers'}</span>
                      </li>
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? 'حلول صيانة مخصصة وعقود خدمة مرنة للأساطيل التجارية' : 'Customized maintenance solutions and flexible CPK fleet contracts'}</span>
                      </li>
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? 'فريق فني معتمد ومدرب من قبل المصنع في كافة أنحاء سلطنة عمان' : 'Factory-certified technicians across Oman authorized workshops'}</span>
                      </li>
                    </ul>
                  </Col>

                  <Col lg={5} className="showcase-image-col">
                    <img 
                      src={resolveImage(totalCareItem?.service_image, '/Images/service/service_total_care.jpg')}
                      alt={totalCareItem?.service_name_en || 'Total Care'}
                      className="showcase-img"
                      onError={(e) => { e.currentTarget.src = '/Images/service/service_total_care.jpg'; }}
                    />
                    <div className="showcase-floating-badge">
                      <FaAward />
                      <span>{lang ? '1,500+ شبكة خدمة خارجية' : '1,500+ Overseas Networks'}</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* Section 2: SERVICE NETWORK METRICS */}
          <section className="service-network-section">
            <Container>
              <div className="section-header-block" data-aos="fade-up">
                <span className="section-eyebrow">
                  {lang ? 'التغطية العالمية والمحلية' : 'Global & Local Footprint'}
                </span>
                <h2 className="section-main-title">
                  {lang ? (networkItem?.service_name_ar || 'شبكة الخدمة العالمية') : (networkItem?.service_name_en || 'SERVICE NETWORK')}
                </h2>
                <p className="section-subtitle-text">
                  {lang 
                    ? (networkItem?.service_description_ar || 'أنشأت فوتون شبكة خدمة خارجية متكاملة في أكثر من 80 دولة ومنطقة، بما يضمن سرعة الاستجابة وتقديم الدعم الفني اللازم لكافة طرازات الشاحنات والحافلات والمركبات التجارية.')
                    : (networkItem?.service_description_en || 'FOTON has established an overseas service network composed of 1,220 service outlets in over 80 countries and regions, covering main regions across Asia, Middle East, Africa, and Europe.')}
                </p>
              </div>

              {/* 4 Stats Grid */}
              <div className="stats-banner-wrapper" data-aos="fade-up">
                <Container>
                  <Row className="g-4">
                    <Col lg={3} sm={6}>
                      <div className="stat-item-box">
                        <FaNetworkWired className="stat-card-icon" />
                        <div className="stat-card-number">158</div>
                        <div className="stat-card-label">{lang ? 'مراكز إدارة الخدمة' : 'Level-1 Outlets'}</div>
                        <div className="stat-card-sub">{lang ? 'مراكز إدارة الخدمة من المستوى الأول' : 'Service Management Centers'}</div>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div className="stat-item-box">
                        <FaTools className="stat-card-icon" />
                        <div className="stat-card-number">1,062</div>
                        <div className="stat-card-label">{lang ? 'وكلاء الخدمة والصيانة' : 'Level-2 Outlets'}</div>
                        <div className="stat-card-sub">{lang ? 'وكلاء ومراكز صيانة معتمدة' : 'Authorized Service Dealers'}</div>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div className="stat-item-box">
                        <FaGlobe className="stat-card-icon" />
                        <div className="stat-card-number">149</div>
                        <div className="stat-card-label">{lang ? 'موزعو المبيعات الرئيسيون' : 'Level-1 Sales Outlets'}</div>
                        <div className="stat-card-sub">{lang ? 'موزعون رسميون حول العالم' : 'Official National Distributors'}</div>
                      </div>
                    </Col>

                    <Col lg={3} sm={6}>
                      <div className="stat-item-box">
                        <FaTruck className="stat-card-icon" />
                        <div className="stat-card-number">1,205</div>
                        <div className="stat-card-label">{lang ? 'وكلاء المبيعات المعتمدون' : 'Level-2 Sales Outlets'}</div>
                        <div className="stat-card-sub">{lang ? 'صالات عرض ومراكز بيع متطورة' : 'Showrooms & Fleet Dealers'}</div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </Container>
          </section>

          {/* Section 3: SERVICE TRAINING */}
          <section className="service-training-section">
            <Container>
              <div className="section-header-block" data-aos="fade-up">
                <span className="section-eyebrow">
                  {lang ? 'التطوير المستمر للكفاءات' : 'Continuous Professional Capability'}
                </span>
                <h2 className="section-main-title">
                  {lang ? (trainingItem?.service_name_ar || 'تدريب وتأهيل الكوادر الفنية') : (trainingItem?.service_name_en || 'SERVICE TRAINING')}
                </h2>
                <p className="section-subtitle-text">
                  {lang 
                    ? (trainingItem?.service_description_ar || 'توفر فوتون تدريباً شاملاً للوكلاء ومراكز الصيانة حول العالم، لضمان أعلى مستوى من الكفاءة والدعم الفني المعتمد.')
                    : (trainingItem?.service_description_en || 'FOTON provides dealers with all-around service training via dedicated training centers around the world. The training team covers over 10 languages and delivers professional courses on service management, parts logistics management, and vehicle engineering.')}
                </p>
              </div>

              <Row className="g-4 alternating-cards-row">
                <Col lg={6} data-aos="fade-up" data-aos-delay="100">
                  <div className="alt-feature-card">
                    <div className="alt-card-img-wrap">
                      <img 
                        src={resolveImage(trainingItem?.service_image, '/Images/service/service_training_centers.jpg')} 
                        alt="Training Centers" 
                        className="alt-card-img"
                        onError={(e) => { e.currentTarget.src = '/Images/service/service_training_centers.jpg'; }}
                      />
                    </div>
                    <div className="alt-card-body">
                      <h3 className="alt-card-title">
                        {lang ? 'مراكز التدريب الإقليمية المجهزة' : 'Regional Service Training Centers'}
                      </h3>
                      <p className="alt-card-text">
                        {lang 
                          ? '12 مركز تدريب إقليمي مجهز بأحدث منصات المحاكاة وأجهزة التشخيص الرقمية، توفر برامج تطبيقية وعملية لتدريب الفنيين والمهندسين على أدق تفاصيل الصيانة الوقائية والإصلاح.'
                          : 'Equipped with digital diagnostic simulators and hands-on mechanical labs across 12 overseas hubs, providing certified practical courses for engineers and technical staff.'}
                      </p>
                    </div>
                  </div>
                </Col>

                <Col lg={6} data-aos="fade-up" data-aos-delay="200">
                  <div className="alt-feature-card">
                    <div className="alt-card-img-wrap">
                      <img 
                        src={resolveImage(trainingItem?.logo_service_image, '/Images/service/service_training_team.jpg')} 
                        alt="Training Team" 
                        className="alt-card-img"
                        onError={(e) => { e.currentTarget.src = '/Images/service/service_training_team.jpg'; }}
                      />
                    </div>
                    <div className="alt-card-body">
                      <h3 className="alt-card-title">
                        {lang ? 'فريق تدريب احترافي متعدد اللغات' : 'Certified Multilingual Training Team'}
                      </h3>
                      <p className="alt-card-text">
                        {lang 
                          ? 'فريق تدريب احترافي يغطي أكثر من 10 لغات، يقدم مسارات تدريبية معتمدة في إدارة ورش الصيانة، وإدارة لوجستيات قطع الغيار، وهندسة المركبات لضمان تجربة خدمة لا مثيل لها.'
                          : 'A dedicated global training team covering more than 10 languages, providing standardized certifications in fleet management, engine maintenance, and electrical architectures.'}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          {/* Section 4: REMOTE & TECHNICAL SUPPORT (IDE) */}
          <section className="service-remote-section py-5">
            <Container>
              <div className="service-showcase-card" data-aos="fade-up">
                <Row className="g-0 align-items-stretch">
                  <Col lg={5} className="showcase-image-col">
                    <img 
                      src={resolveImage(remoteSupportItem?.service_image, '/Images/service/service_remote_support.jpg')}
                      alt={remoteSupportItem?.service_name_en || 'Remote Support'}
                      className="showcase-img"
                      onError={(e) => { e.currentTarget.src = '/Images/service/service_remote_support.jpg'; }}
                    />
                    <div className="showcase-floating-badge">
                      <FaHeadset />
                      <span>{lang ? 'نظام IDE الذكي' : 'IDE Expert System'}</span>
                    </div>
                  </Col>

                  <Col lg={7} className="showcase-text-col">
                    <span className="showcase-badge">
                      {lang ? 'التشخيص الذكي السريع' : 'Intelligent Diagnostics Experts'}
                    </span>
                    <h2 className="showcase-title">
                      {lang ? (remoteSupportItem?.service_name_ar || 'الدعم الفني والتشخيص الذكي عن بعد') : (remoteSupportItem?.service_name_en || 'REMOTE & TECHNICAL SUPPORT')}
                    </h2>
                    <p className="showcase-desc">
                      {lang 
                        ? (remoteSupportItem?.service_description_ar || 'أطلقت فوتون نظام التشخيص الذكي المتخصص (IDE) عبر الويب وتطبيقات الأجهزة الذكية، لتقديم الدعم الفني الفوري، وتشخيص الأعطال، والاستشارات الفنية المباشرة لضمان سرعة الصيانة ودقة الإصلاح.')
                        : (remoteSupportItem?.service_description_en || 'FOTON officially launched the IDE (Intelligent Diagnostics Experts) expert diagnosis system online on web and mobile segments simultaneously. Functions include circuit diagram query, vehicle fault diagnosis and DTC query, maintenance procedures, and real-time remote expert consultation for frontline technicians to solve technical challenges quickly.')}
                    </p>

                    <Row className="g-3 mt-1">
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">⚡ {lang ? 'مخططات الدوائر الكهربائية' : 'Circuit Diagram Query'}</h6>
                          <small className="text-muted">{lang ? 'استعلام فوري وتفاعلي عن المخططات الهندسية' : 'Instant digital wiring and schematics reference'}</small>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">⚡ {lang ? 'تشخيص رموز الأعطال DTC' : 'Real-Time DTC Query'}</h6>
                          <small className="text-muted">{lang ? 'تحديد سبب الخلل البرمجي أو الميكانيكي بسرعة' : 'Automated fault code diagnosis and step guide'}</small>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">⚡ {lang ? 'إجراءات الصيانة المعيارية' : 'Standard Maintenance Guides'}</h6>
                          <small className="text-muted">{lang ? 'خطوات عمل دقيقة وموثقة من المصنع' : 'Standard operating procedures for technicians'}</small>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">⚡ {lang ? 'استشارات فنية مباشرة' : 'Live Expert Consultation'}</h6>
                          <small className="text-muted">{lang ? 'تواصل فوري بين ورش الصيانة وخبراء المصنع' : 'Direct live video connection with factory engineers'}</small>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* Any additional service items added dynamically via Admin */}
          {extraServices.length > 0 && (
            <section className="extra-services-section py-4">
              <Container>
                <Row className="g-4">
                  {extraServices.map((item, idx) => (
                    <Col lg={6} key={item.id || idx} data-aos="fade-up">
                      <div className="service-showcase-card h-100">
                        {item.service_image && (
                          <div style={{ height: '220px', overflow: 'hidden' }}>
                            <img 
                              src={resolveImage(item.service_image)} 
                              alt={item.service_name_en} 
                              className="showcase-img"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="showcase-title fs-4">
                            {lang ? (item.service_name_ar || item.service_name_en) : item.service_name_en}
                          </h3>
                          <p className="showcase-desc">
                            {lang ? (item.service_description_ar || item.service_description_en) : item.service_description_en}
                          </p>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Container>
            </section>
          )}

          {/* Service CTA Banner */}
          <Container>
            <div className="services-cta-banner" data-aos="fade-up">
              <div className="cta-text-side">
                <h3>{lang ? 'هل تحتاج إلى صيانة أو فحص لمركبتك في عمان؟' : 'Need Service or Inspection for your Fleet in Oman?'}</h3>
                <p>{lang ? 'احجز موعد الصيانة اليوم مع رمال الخليج الكبرى – الموزع الرسمي لـ فوتون في سلطنة عمان، وتمتع بخدمة معتمدة وأسعار شفافة.' : 'Book an appointment today with Gulf Great Sands – Official Distributor of Foton in Oman for expert certified care.'}</p>
              </div>
              <div className="cta-action-buttons">
                <a href="tel:80077700" className="btn-cta-primary">
                  <FaPhoneAlt />
                  <span>{lang ? 'اتصل بمركز الخدمة' : 'Call Workshop: 80077700'}</span>
                </a>
                <a href="https://wa.me/96892888241" target="_blank" rel="noopener noreferrer" className="btn-cta-whatsapp">
                  <FaWhatsapp />
                  <span>{lang ? 'واتساب مركز الصيانة' : 'WhatsApp Service Advisor'}</span>
                </a>
              </div>
            </div>
          </Container>

        </main>
      )}

      {/* ========================================================
          TAB 2: FOTON GENUINE PARTS
          ======================================================== */}
      {activeTab === 'parts' && (
        <main className="tab-content-parts">
          
          {/* Section 1: GENUINE PARTS SHOWCASE */}
          <section className="service-showcase-section">
            <Container>
              <div className="service-showcase-card" data-aos="fade-up">
                <Row className="g-0 align-items-stretch">
                  <Col lg={7} className="showcase-text-col">
                    <span className="showcase-badge">
                      {lang ? 'قطع الغيار الأصلية المعتمدة' : 'Official Manufacturer Spares'}
                    </span>
                    <h2 className="showcase-title">
                      {lang ? (genuinePartsItem?.service_name_ar || 'قطع غيار فوتون الأصلية') : (genuinePartsItem?.service_name_en || 'FOTON GENUINE PARTS')}
                    </h2>
                    <p className="showcase-desc">
                      {lang 
                        ? (genuinePartsItem?.service_description_ar || 'أنشأت مجموعة فوتون موتور مراكز لوجستية كبرى لقطع الغيار مجهزة بأحدث أنظمة إدارة المستودعات العالمية، مع مخزون متكامل يضم أكثر من 160,000 نوع من قطع الغيار الأصلية لكافة طرازات فوتون التجارية.')
                        : (genuinePartsItem?.service_description_en || 'Foton Motor Group has established major parts logistics centers covering world-class warehousing management systems, elevated three-dimensional warehouses, barcode reading technology, and automated logistics. Our global inventory features over 160,000 types of genuine after-sales parts covering all commercial vehicle models, guaranteeing optimal durability, safety, and performance.')}
                    </p>

                    <ul className="showcase-features-list">
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? 'أكثر من 160,000 نوع من قطع الغيار الأصلية لتغطية كافة طرازات فوتون' : 'Over 160,000 types of after-sales genuine parts for all series'}</span>
                      </li>
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? 'مستودعات ثلاثية الأبعاد مؤتمتة بنظام الباركود وتقنيات التخزين الذكي' : 'Automated 3D warehousing systems with barcode precision tracking'}</span>
                      </li>
                      <li className="showcase-feature-item">
                        <FaCheckCircle />
                        <span>{lang ? 'توريد مباشر في سلطنة عمان عبر رمال الخليج الكبرى بأسعار تنافسية' : 'Direct supply across Oman through Gulf Great Sands with warranty'}</span>
                      </li>
                    </ul>
                  </Col>

                  <Col lg={5} className="showcase-image-col">
                    <img 
                      src={resolveImage(genuinePartsItem?.service_image, '/Images/service/parts_genuine.jpg')}
                      alt={genuinePartsItem?.service_name_en || 'Foton Genuine Parts'}
                      className="showcase-img"
                      onError={(e) => { e.currentTarget.src = '/Images/service/parts_genuine.jpg'; }}
                    />
                    <div className="showcase-floating-badge">
                      <FaWarehouse />
                      <span>{lang ? '160,000+ صنف متوفر' : '160,000+ Spare Parts'}</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* Section 2: 4 PILLARS OF FOTON GENUINE PARTS */}
          <section className="pillars-grid-section">
            <Container>
              <div className="section-header-block" data-aos="fade-up">
                <span className="section-eyebrow">
                  {lang ? 'ركائز الجودة والاعتمادية' : '4 Pillars of Genuine Parts'}
                </span>
                <h2 className="section-main-title">
                  {lang ? 'لماذا تختار قطع غيار فوتون الأصلية؟' : 'THE 4 PILLARS OF FOTON GENUINE PARTS'}
                </h2>
                <p className="section-subtitle-text">
                  {lang 
                    ? 'صممت قطع غيار فوتون الأصلية لتلبي أدق المعايير الهندسية للمصنع، مما يضمن أعلى درجات الأمان وأطول عمر تشغيلي لأسطولكم.'
                    : 'Engineered specifically for your commercial vehicle to preserve factory tolerances, fuel efficiency, and structural safety.'}
                </p>
              </div>

              <Row className="g-4">
                <Col lg={3} sm={6} data-aos="fade-up" data-aos-delay="100">
                  <div className="pillar-card">
                    <div className="pillar-icon-box">
                      <FaAward />
                    </div>
                    <h3 className="pillar-title">{lang ? 'جودة موثوقة' : 'Reliable Quality'}</h3>
                    <p className="pillar-desc">
                      {lang 
                        ? 'اختبارات صارمة في أصعب ظروف التشغيل ودرجات الحرارة العالية لضمان المتانة والأداء المثالي.'
                        : 'Precision-tested under harsh extreme environmental and load conditions to match strict factory standards.'}
                    </p>
                  </div>
                </Col>

                <Col lg={3} sm={6} data-aos="fade-up" data-aos-delay="200">
                  <div className="pillar-card">
                    <div className="pillar-icon-box">
                      <FaCogs />
                    </div>
                    <h3 className="pillar-title">{lang ? 'تشكيلة ضخمة' : 'Huge Assortment'}</h3>
                    <p className="pillar-desc">
                      {lang 
                        ? 'أكثر من 160,000 نوع من القطع لتغطية كافة طرازات الشاحنات الثقيلة والخفيفة والمركبات التجارية.'
                        : 'Over 160,000 component varieties covering heavy trucks, light commercial vehicles, and passenger vans.'}
                    </p>
                  </div>
                </Col>

                <Col lg={3} sm={6} data-aos="fade-up" data-aos-delay="300">
                  <div className="pillar-card">
                    <div className="pillar-icon-box">
                      <FaWarehouse />
                    </div>
                    <h3 className="pillar-title">{lang ? 'نظام توزيع عالمي' : 'Distribution System'}</h3>
                    <p className="pillar-desc">
                      {lang 
                        ? 'مراكز توزيع إقليمية (RDC) في دبي وألمانيا وبنما، مع دورة استيراد سريعة ومعدل تلبية يتجاوز 98%.'
                        : 'Regional Distribution Centers in Dubai, Germany, and Panama with 7-25 days replenishment and 98%+ fulfillment.'}
                    </p>
                  </div>
                </Col>

                <Col lg={3} sm={6} data-aos="fade-up" data-aos-delay="400">
                  <div className="pillar-card">
                    <div className="pillar-icon-box">
                      <RiSpeedUpFill />
                    </div>
                    <h3 className="pillar-title">{lang ? 'نظام OTD المتطور' : 'OTD System'}</h3>
                    <p className="pillar-desc">
                      {lang 
                        ? 'مستودعات مرتفعة ذكية، قراءة إلكترونية بالباركود، ونظام تسليم فوري في الوقت المحدد لتقليل توقف المركبة.'
                        : 'Elevated 3D automated warehouses with instant barcode picking technology for rapid order turnaround.'}
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          {/* Section 3: GLOBAL PARTS SUPPLY & DISTRIBUTION CENTERS */}
          <section className="service-supply-section py-5">
            <Container>
              <div className="service-showcase-card" data-aos="fade-up">
                <Row className="g-0 align-items-stretch">
                  <Col lg={5} className="showcase-image-col">
                    <img 
                      src={resolveImage(partsSupplyItem?.service_image, '/Images/service/parts_centers.jpg')}
                      alt={partsSupplyItem?.service_name_en || 'Global Supply'}
                      className="showcase-img"
                      onError={(e) => { e.currentTarget.src = '/Images/service/parts_centers.jpg'; }}
                    />
                    <div className="showcase-floating-badge">
                      <FaTruck />
                      <span>{lang ? 'إمداد إقليمي مباشر' : 'RDC Regional Supply'}</span>
                    </div>
                  </Col>

                  <Col lg={7} className="showcase-text-col">
                    <span className="showcase-badge">
                      {lang ? 'الشبكة اللوجستية الدولية' : 'International Logistics'}
                    </span>
                    <h2 className="showcase-title">
                      {lang ? (partsSupplyItem?.service_name_ar || 'نظام إمداد وتوزيع قطع الغيار العالمي') : (partsSupplyItem?.service_name_en || 'GLOBAL PARTS SUPPLY')}
                    </h2>
                    <p className="showcase-desc">
                      {lang 
                        ? (partsSupplyItem?.service_description_ar || 'يتم ضمان إمداد قطع الغيار في الخارج عبر مراكز توزيع إقليمية في دبي وألمانيا وبنما، مما يضمن تلبية أكثر من 85% من الطلبات بشكل فوري وسرعة توفير القطع المطلوبة للعملاء في سلطنة عمان.')
                        : (partsSupplyItem?.service_description_en || 'Overseas parts supply is guaranteed through Regional Distribution Centers (RDC) in Dubai, Germany, and Panama, and National Distribution Centers across 6+ international hubs. Over 85% of standard parts orders are fulfilled immediately, with a procurement cycle of 7-25 days and over 98% fulfillment rate within 30 days.')}
                    </p>

                    <Row className="g-3 mt-1">
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">📦 {lang ? 'مركز التوزيع الإقليمي في دبي' : 'RDC Dubai (Middle East Hub)'}</h6>
                          <small className="text-muted">{lang ? 'تلبية فورية وشحن سريع لجميع مناطق سلطنة عمان' : 'Rapid dispatch and fulfillment across Oman'}</small>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="p-3 bg-light rounded border">
                          <h6 className="fw-bold mb-1 text-primary">📦 {lang ? 'مراكز التوزيع الوطنية (NDC)' : 'National Distribution Centers'}</h6>
                          <small className="text-muted">{lang ? 'مخزون استراتيجي يلبي أكثر من 85% فورياً' : 'Over 85% immediate first-pick availability'}</small>
                        </div>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* Section 4: 3 CORE VALUES (OFFICIAL, QUALITY, PRICE) */}
          <section className="core-values-section">
            <Container>
              <div className="section-header-block" data-aos="fade-up">
                <span className="section-eyebrow">
                  {lang ? 'معايير القيمة الأساسية' : 'Why Quality Parts'}
                </span>
                <h2 className="section-main-title">
                  {lang ? (whyQualityPartsItem?.service_name_ar || 'لماذا قطع الغيار الأصلية؟') : (whyQualityPartsItem?.service_name_en || 'WHY QUALITY PARTS')}
                </h2>
                <p className="section-subtitle-text">
                  {lang 
                    ? (whyQualityPartsItem?.service_description_ar || 'ضمان معتمد، جودة فائقة، وأسعار منافسة: تصنع قطع غيار فوتون الأصلية وفقاً لأدق المعايير الهندسية للمصنع لضمان أقصى درجات الأمان والاعتمادية وأطول عمر تشغيلي لمركباتكم.')
                    : (whyQualityPartsItem?.service_description_en || 'Official Warranty, Reliable Quality, and Competitive Pricing: Genuine Foton parts are engineered to exact factory tolerances. With rigorous R&D quality audits, manufacturer warranty replacement, and direct supply through Gulf Great Sands, customers achieve lower total cost of ownership and uninterrupted uptime.')}
                </p>
              </div>

              <Row className="g-4">
                <Col lg={4} md={6} data-aos="fade-up" data-aos-delay="100">
                  <div className="core-value-card">
                    <div className="core-value-badge">
                      <FaAward />
                    </div>
                    <h3 className="core-value-title">{lang ? 'ضمان رسمي معتمد' : '1. OFFICIAL'}</h3>
                    <p className="core-value-desc">
                      {lang 
                        ? 'قطع معتمدة ومضمونة من المصنع عبر الموزع الرسمي "رمال الخليج الكبرى"، مع استبدال مجاني في حال وجود أي عيب مصنعي.'
                        : 'Direct from the manufacturer through authorized distributor Gulf Great Sands, covered by genuine factory replacement warranty.'}
                    </p>
                  </div>
                </Col>

                <Col lg={4} md={6} data-aos="fade-up" data-aos-delay="200">
                  <div className="core-value-card">
                    <div className="core-value-badge">
                      <FaShieldAlt />
                    </div>
                    <h3 className="core-value-title">{lang ? 'جودة فائقة وموثوقة' : '2. QUALITY'}</h3>
                    <p className="core-value-desc">
                      {lang 
                        ? 'تطابق هندسي تام يحمي المحرك وناقل الحركة من الأعطال الجانبية، ويحافظ على كفاءة استهلاك الوقود لأطول فترة تشغيل.'
                        : 'Exact engineering fitment prevents premature wear and collateral damage, extending engine and transmission service life.'}
                    </p>
                  </div>
                </Col>

                <Col lg={4} md={6} data-aos="fade-up" data-aos-delay="300">
                  <div className="core-value-card">
                    <div className="core-value-badge">
                      <RiShieldCheckFill />
                    </div>
                    <h3 className="core-value-title">{lang ? 'أسعار منافسة وشفافة' : '3. PRICE'}</h3>
                    <p className="core-value-desc">
                      {lang 
                        ? 'أسعار واضحة ومدروسة تضمن تحقيق أقل تكلفة إجمالية للملكية (TCO) للأفراد والشركات في سلطنة عمان.'
                        : 'Transparent competitive pricing delivering lowest total cost of ownership and maximizing commercial fleet uptime.'}
                    </p>
                  </div>
                </Col>
              </Row>
            </Container>
          </section>

          {/* Section 5: AUTOMATE PACKAGING & SECURITY */}
          <section className="service-packaging-section py-4">
            <Container>
              <div className="service-showcase-card" data-aos="fade-up">
                <Row className="g-0 align-items-stretch">
                  <Col lg={7} className="showcase-text-col">
                    <span className="showcase-badge">
                      {lang ? 'الحماية والأصالة' : 'Anti-Counterfeit Protection'}
                    </span>
                    <h2 className="showcase-title">
                      {lang ? (packagingItem?.service_name_ar || 'تغليف وحماية قطع الغيار الأصلية') : (packagingItem?.service_name_en || 'AUTOMATE PACKAGING & QUALITY')}
                    </h2>
                    <p className="showcase-desc">
                      {lang 
                        ? (packagingItem?.service_description_ar || 'قامت فوتون بترقية وتطوير تغليف قطع الغيار الأصلية بعلامات أمنية ثلاثية الأبعاد ورموز تحقق إلكترونية لحماية المستهلك وضمان أصالة وجودة كل قطعة.')
                        : (packagingItem?.service_description_en || 'Foton has fully upgraded the packaging of genuine parts, highlighting authentic holographic security branding, barcode verification, and moisture-resistant packaging that enables consumers to identify authentic components with confidence.')}
                    </p>

                    <Row className="g-3 mt-2">
                      <Col sm={6}>
                        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border">
                          <FaBarcode className="fs-3 text-primary flex-shrink-0" />
                          <div>
                            <strong className="d-block text-dark">{lang ? 'رمز باركود فريد' : '2D Barcode Verification'}</strong>
                            <small className="text-muted">{lang ? 'تحقق رقمي فوري من المنشأ والأصالة' : 'Instant digital authenticity scan'}</small>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="d-flex align-items-center gap-3 p-3 bg-light rounded border">
                          <FaShieldAlt className="fs-3 text-primary flex-shrink-0" />
                          <div>
                            <strong className="d-block text-dark">{lang ? 'ملصق هولوغرام أمني' : 'Hologram Security Seal'}</strong>
                            <small className="text-muted">{lang ? 'شعار ثلاثي الأبعاد مضاد للتزييف' : 'Tamper-evident authentic label'}</small>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  <Col lg={5} className="showcase-image-col">
                    <img 
                      src={resolveImage(packagingItem?.service_image, '/Images/service/parts_packaging_1.jpg')}
                      alt={packagingItem?.service_name_en || 'Packaging'}
                      className="showcase-img"
                      onError={(e) => { e.currentTarget.src = '/Images/service/parts_packaging_1.jpg'; }}
                    />
                    <div className="showcase-floating-badge">
                      <FaBarcode />
                      <span>{lang ? 'تغليف رسمي موثق' : 'Official Packaging'}</span>
                    </div>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          {/* Any additional parts items added dynamically via Admin */}
          {extraParts.length > 0 && (
            <section className="extra-parts-section py-4">
              <Container>
                <Row className="g-4">
                  {extraParts.map((item, idx) => (
                    <Col lg={6} key={item.id || idx} data-aos="fade-up">
                      <div className="service-showcase-card h-100">
                        {item.service_image && (
                          <div style={{ height: '220px', overflow: 'hidden' }}>
                            <img 
                              src={resolveImage(item.service_image)} 
                              alt={item.service_name_en} 
                              className="showcase-img"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="showcase-title fs-4">
                            {lang ? (item.service_name_ar || item.service_name_en) : item.service_name_en}
                          </h3>
                          <p className="showcase-desc">
                            {lang ? (item.service_description_ar || item.service_description_en) : item.service_description_en}
                          </p>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Container>
            </section>
          )}

          {/* Parts Quotation CTA Banner */}
          <Container>
            <div className="services-cta-banner" data-aos="fade-up">
              <div className="cta-text-side">
                <h3>{lang ? 'تبحث عن قطع غيار فوتون أصلية لمركبتك؟' : 'Looking for Foton Genuine Spare Parts?'}</h3>
                <p>{lang ? 'تواصل مباشرة مع قسم قطع الغيار في رمال الخليج الكبرى للحصول على عرض سعر فوري وتسليم سريع في كافة محافظات عمان.' : 'Contact Gulf Great Sands Parts Department for immediate quotations and swift delivery across Oman.'}</p>
              </div>
              <div className="cta-action-buttons">
                <a href="tel:80077700" className="btn-cta-primary">
                  <FaPhoneAlt />
                  <span>{lang ? 'اتصل بقسم قطع الغيار' : 'Call Parts Desk: 80077700'}</span>
                </a>
                <a href="https://wa.me/96892888241" target="_blank" rel="noopener noreferrer" className="btn-cta-whatsapp">
                  <FaWhatsapp />
                  <span>{lang ? 'طلب تسعيرة عبر الواتساب' : 'WhatsApp Parts Inquiry'}</span>
                </a>
              </div>
            </div>
          </Container>

        </main>
      )}

      {/* FOOTER */}
      <Footer />
    </div>
  );
}

export default Services;
